/**
 * CSV Configuration Exporter
 * Exports existing tile configurations to CSV format for bulk editing
 */
import { Tile, TileFormat, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL, TILE_TYPE_SUPER_FOLDER } from '../../../../shared/models/tile';
import { DeviceType } from '../../../../shared/models/device';
import { PROTOCOL_CSV_CONFIG, TILE_CREATION_CONFIG_COLUMNS, TILE_CONFIGURATION_COLUMNS, NUMERIC_SPECIFIC_COLUMNS, TOGGLE_SPECIFIC_COLUMNS, FORMULA_CREATION_CONFIG_COLUMNS, FORMULA_NUMERIC_SPECIFIC_COLUMNS, CsvFieldId, getFormulaBindingColumns } from '../constants/csv-columns.constants';
import { TAG_PROFILES } from '../../../../shared/constants/tag-profiles.constants';
import { FORMULA_TEMPLATES } from '@features/formula-tile/shared/constants/formula-templates.constants';
import { FormulaType, FORMULA_MAX_BINDINGS } from '@shared/models/formula-config';
import * as Papa from 'papaparse';
import { SimpleAnalogStructure, SimpleBoolStructure, ConditionMode } from '../../../../shared/models/structure';
import { findAnalogCondition } from '../../../../shared/utils/condition.util';
import { CompareOperator } from '../../../../shared/constants/tile-config.constants';
import { FileDownloadUtil } from '../../../../shared/utils/file-download.util';
import { Observable } from 'rxjs';

export interface TileConfigExportOptions {
  tiles: Tile[];
  protocol?: DeviceType;
  /** Map of tile ID to array of app IDs that contain this tile */
  tileAppMap?: Map<string, string[]>;
}

/**
 * CSV Configuration Exporter
 * Generates CSV files from existing tile configurations
 */
export class CSVConfigExporter {

  /**
   * Export tiles to CSV format
   * Tiles must be of the same type (numeric or toggle)
   */
  static exportTilesToCSV(options: TileConfigExportOptions): any[] {
    const { tiles, protocol, tileAppMap } = options;

    if (!tiles || tiles.length === 0) {
      throw new Error('No tiles provided for export');
    }

    // Determine tile format from first tile
    const tileFormat = this.getTileFormat(tiles[0]);

    if (tileFormat === TileFormat.Empty) {
      throw new Error('Cannot export folder tiles');
    }

    // Validate all tiles are same format
    const allSameFormat = tiles.every(tile => this.getTileFormat(tile) === tileFormat);
    if (!allSameFormat) {
      throw new Error('All tiles must be of the same type (numeric or toggle)');
    }

    // Get columns for this tile type
    const columns = this.getColumnsForTileFormat(tileFormat, protocol);

    // Convert each tile to CSV row
    const rows = tiles.map(tile => this.tileToCSVRow(tile, columns, protocol, tileAppMap));

    return rows;
  }

  /**
   * Download tiles as CSV file
   */
  static downloadTilesCSV(options: TileConfigExportOptions, filename?: string): Observable<void> {
    const rows = this.exportTilesToCSV(options);
    const csv = Papa.unparse(rows);
    return this.downloadCsvText(csv, filename || `tile-configurations-${Date.now()}.csv`);
  }

  /**
   * Download empty numeric configuration template
   */
  static downloadNumericConfigTemplate(protocol?: DeviceType): Observable<void> {
    const columns = this.getColumnsForTileFormat(TileFormat.Numeric, protocol);
    const emptyRow: any = {};
    columns.forEach(col => emptyRow[col] = '');

    const csv = Papa.unparse([emptyRow]);
    return this.downloadCsvText(csv, 'numeric-tile-configuration-template.csv');
  }

  /**
   * Download empty toggle configuration template
   */
  static downloadToggleConfigTemplate(protocol?: DeviceType): Observable<void> {
    const columns = this.getColumnsForTileFormat(TileFormat.Toggle, protocol);
    const emptyRow: any = {};
    columns.forEach(col => emptyRow[col] = '');

    const csv = Papa.unparse([emptyRow]);
    return this.downloadCsvText(csv, 'toggle-tile-configuration-template.csv');
  }

  /**
   * Download empty formula tile creation template.
   * Pass a template ID (e.g. 'dew-point-f') to pre-fill formula identity and variable keys.
   * Pass null for a custom formula template (3 empty binding sets).
   */
  static downloadFormulaConfigTemplate(templateId: string | null): Observable<void> {
    const template = templateId ? FORMULA_TEMPLATES.find(t => t.id === templateId) ?? null : null;
    const row: any = {};

    row[CsvFieldId.FORMULA_TYPE] = template ? FormulaType.Template : FormulaType.Custom;
    row[CsvFieldId.FORMULA_VALUE] = template ? templateId : '';

    const bindingCount = template ? template.variables.length : FORMULA_MAX_BINDINGS;
    for (let n = 1; n <= bindingCount; n++) {
      getFormulaBindingColumns(n).forEach(col => row[col] = '');
      if (template) {
        row[`Binding ${n}: Variable`] = template.variables[n - 1].key;
      }
    }

    FORMULA_CREATION_CONFIG_COLUMNS.forEach(col => row[col] = '');
    FORMULA_NUMERIC_SPECIFIC_COLUMNS.forEach(col => row[col] = '');

    const filename = template
      ? `formula-tile-template-${template.id}.csv`
      : 'formula-tile-template-custom.csv';

    return this.downloadCsvText(Papa.unparse([row]), filename);
  }

  /**
   * Download raw CSV content (already formatted as CSV string)
   * Use this when you already have CSV text from an API or other source
   */
  static downloadCsvText(csvContent: string, filename: string): Observable<void> {
    return FileDownloadUtil.downloadCsv(csvContent, filename);
  }

  /**
   * Get tile format (numeric or toggle)
   */
  private static getTileFormat(tile: Tile): TileFormat {
    if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
      return TileFormat.Empty;
    }

    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      return TileFormat.Numeric;
    }

    if (tile.Type === TILE_TYPE_SIMPLE_BOOL) {
      return TileFormat.Toggle;
    }

    return TileFormat.Empty;
  }

  /**
   * Get CSV columns for tile format
   */
  private static getColumnsForTileFormat(format: TileFormat, protocol?: DeviceType): string[] {
    // Start with Tile ID column (required for updates)
    const columns: string[] = [CsvFieldId.TILE_ID];

    // Add protocol-specific columns if protocol is specified
    if (protocol && PROTOCOL_CSV_CONFIG[protocol]?.[format]) {
      const protocolConfig = PROTOCOL_CSV_CONFIG[protocol][format];
      columns.push(...protocolConfig.columns);
    } else {
      // Add common tile configuration columns
      columns.push(...TILE_CONFIGURATION_COLUMNS);

      // Add format-specific columns
      if (format === TileFormat.Numeric) {
        columns.push(...NUMERIC_SPECIFIC_COLUMNS);
      } else if (format === TileFormat.Toggle) {
        columns.push(...TOGGLE_SPECIFIC_COLUMNS);
      }
    }

    return columns;
  }

  /**
   * Convert tile to CSV row
   */
  private static tileToCSVRow(tile: Tile, columns: string[], protocol?: DeviceType, tileAppMap?: Map<string, string[]>): any {
    const row: any = {};

    // Initialize all columns
    columns.forEach(col => row[col] = '');

    // Tile ID (required for configuration updates)
    row[CsvFieldId.TILE_ID] = tile.ID;

    // Common tile properties
    row[CsvFieldId.TILE_NAME] = tile.Name || '';
    row[CsvFieldId.DISPLAY_NAME] = tile.Appearance?.DisplayName || '';
    row[CsvFieldId.TILE_WRITABILITY] = String((tile.Structure as any)?.Writable ?? false);
    row[CsvFieldId.COLOR] = tile.Appearance?.Color || '';
    row[CsvFieldId.ICON] = tile.Appearance?.Icon || '';

    // Linked apps (informational only in configuration CSVs)
    if (columns.includes('Linked App IDs')) {
      const linkedAppIds = tileAppMap?.get(tile.ID) || [];
      row[CsvFieldId.LINKED_APP_IDS] = linkedAppIds.join(',');
    }

    // Auto-clear alarms
    row[CsvFieldId.AUTO_CLEAR_ALARMS] = (tile.Structure as any)?.AutoClear ?? '';

    // Profile, tags and facets (pre-populated with current assignments)
    if (columns.includes(CsvFieldId.PROFILE)) {
      const profileKey = Object.keys(TAG_PROFILES).find(key =>
        tile.facets.some(f => TAG_PROFILES[key].Facets.some(pf => pf.ID === f.ID))
      );
      row[CsvFieldId.PROFILE] = profileKey ? TAG_PROFILES[profileKey].Label : '';
    }
    if (columns.includes(CsvFieldId.TAGS)) {
      row[CsvFieldId.TAGS] = tile.tags.map(t => t.Label).join('|');
    }
    if (columns.includes(CsvFieldId.FACETS)) {
      row[CsvFieldId.FACETS] = tile.facets.map(f => `${f.Label}=${f.Value}`).join('|');
    }

    // Protocol-specific fields
    if (protocol && tile.Structure) {
      this.addProtocolSpecificFields(row, tile, protocol);
    }

    // Format-specific fields
    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      this.addNumericFields(row, tile);
    } else if (tile.Type === TILE_TYPE_SIMPLE_BOOL) {
      this.addToggleFields(row, tile);
    }

    return row;
  }

  /**
   * Add protocol-specific fields to CSV row
   */
  private static addProtocolSpecificFields(row: any, tile: Tile, protocol: DeviceType): void {
    const structure = tile.Structure as any;

    if (protocol === DeviceType.BACnet && structure.BACnetData) {
      row[CsvFieldId.MOTION_DEVICE_ID] = structure.BACnetData.DeviceID || '';
      row[CsvFieldId.BACNET_INSTANCE_NUMBER] = structure.BACnetData.InstanceNumber || '';
      row[CsvFieldId.BACNET_OBJECT_TYPE] = structure.BACnetData.ObjectType || '';
    } else if (protocol === DeviceType.Modbus && structure.ModbusData) {
      row[CsvFieldId.MOTION_DEVICE_ID] = structure.ModbusData.DeviceID || '';
      row[CsvFieldId.MODBUS_ADDRESS] = structure.ModbusData.PointID || '';
      row[CsvFieldId.MODBUS_DATA_TYPE] = structure.ModbusData.DataType || '';
      row[CsvFieldId.MODBUS_BYTE_ORDER] = structure.ModbusData.ByteOrder || '';
      row[CsvFieldId.MODBUS_FUNCTIONS] = String(structure.ModbusData.Writable ?? false);
    } else if (protocol === DeviceType.LoRaWAN && structure.LoRaWANData) {
      row[CsvFieldId.MOTION_DEVICE_ID] = structure.LoRaWANData.DeviceID || '';
      row[CsvFieldId.LORAWAN_POINT_NAME] = structure.LoRaWANData.PointName || '';
      row[CsvFieldId.LORAWAN_DATA_SOURCE] = structure.LoRaWANData.PointType || '';
      row[CsvFieldId.LORAWAN_DATA_TYPE] = structure.LoRaWANData.PointFormat || '';
    }
  }

  /**
   * Add numeric tile specific fields
   */
  private static addNumericFields(row: any, tile: Tile): void {
    const appearance = tile.Appearance;
    const structure = tile.Structure as SimpleAnalogStructure;

    // Data display
    row[CsvFieldId.DATA_DISPLAY_UNITS] = (structure as any)?.Units || '';
    row[CsvFieldId.DECIMAL_PLACES] = String(appearance?.Config?.Decimal ?? 2);

    // Transformation
    row[CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA] = appearance?.Config?.Conversion || '';
    row[CsvFieldId.MULTIPLIER] = String(appearance?.Config?.Multiplier ?? 1);
    row[CsvFieldId.CALIBRATION_OFFSET] = String(appearance?.Config?.Offset ?? 0);

    // Writable range
    row[CsvFieldId.MINIMUM_WRITABLE_VALUE] = String(appearance?.Config?.MinAllowed ?? '');
    row[CsvFieldId.MAXIMUM_WRITABLE_VALUE] = String(appearance?.Config?.MaxAllowed ?? '');

    // Alarm conditions — find each condition by Mode + operator direction rather than
    // by array index, because the tile editor reorders the array on save.
    if (structure?.Conditions?.length) {
      const c = structure.Conditions;
      this.addConditionFields(row, findAnalogCondition(c, ConditionMode.Critical, 'low'), 'Low Critical');
      this.addConditionFields(row, findAnalogCondition(c, ConditionMode.Warning, 'low'), 'Low Warning');
      this.addConditionFields(row, findAnalogCondition(c, ConditionMode.Warning, 'high'), 'High Warning');
      this.addConditionFields(row, findAnalogCondition(c, ConditionMode.Critical, 'high'), 'High Critical');
    }
  }

  /**
   * Add toggle tile specific fields
   */
  private static addToggleFields(row: any, tile: Tile): void {
    const structure = tile.Structure as SimpleBoolStructure;

    // Data display phrases
    row[CsvFieldId.DATA_DISPLAY_TRUE_PHRASE] = (structure as any)?.TruePhrase || '';
    row[CsvFieldId.DATA_DISPLAY_FALSE_PHRASE] = (structure as any)?.FalsePhrase || '';

    // Critical alarm (single condition for toggle tiles)
    if (structure?.Conditions && structure.Conditions.length > 0) {
      this.addConditionFields(row, structure.Conditions[0], 'Critical');
    }
  }

  /**
   * Add alarm condition fields to CSV row
   */
  private static addConditionFields(row: any, condition: any, prefix: string): void {
    row[`${prefix}: Enabled`] = String(condition?.Enabled ?? false);
    row[`${prefix}: Operator`] = condition?.Comparison?.Operator || CompareOperator.GreaterThan;
    row[`${prefix}: Value`] = String(condition?.Comparison?.Value ?? '');
    row[`${prefix}: Delay`] = String(condition?.Delay ?? 0);
    row[`${prefix}: Message`] = condition?.DisplayText || '';
    row[`${prefix}: Icon`] = condition?.Icon || '';
  }
}
